import _ from 'lodash';
import { decamelizeKeys } from '../../lib';
import {
    SplitChargeSnapshot,
    SplitChargeCreationSnapshot,
    SplitChargeSimulationSnapshot,
    RentalResultWithDestination,
    SimulateSplitChargeArgs,
    SplitChargeCreationOutput,
} from '../../models/RentalPayment';
import api from '../api';
import { getBackVersion } from '../../config';

const getSplitCharges = (
    contractId: string
): Promise<RentalResultWithDestination<SplitChargeSnapshot[]>> =>
    api.request<RentalResultWithDestination<SplitChargeSnapshot[]>>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/split_charges`,
    });

const getSplitCharge = (
    contractId: string,
    splitChargeId: number
): Promise<SplitChargeSnapshot> =>
    api.request<SplitChargeSnapshot>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/split_charges/${splitChargeId}`,
    });

const toApiOutput = (
    charge: SplitChargeCreationSnapshot
): SplitChargeCreationOutput => {
    return {
        ..._.omit(charge, ['installments']),
        referenceMonths: charge.installments?.map(i => ({
            referenceMonth: i.referenceMonth,
            isSettled: i.status === 'settled',
        })),
    };
};

const simulateSplitCharge = ({
    contractId,
    referenceMonthType,
    monthDueDay,
    contractBeginDate,
    contractEndDate,
    charge,
    splitChargeId,
}: SimulateSplitChargeArgs): Promise<
    Omit<SplitChargeSimulationSnapshot, 'id'>
> =>
    api.request<Omit<SplitChargeSimulationSnapshot, 'id'>>({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/split_charges/simulation`,
        data: decamelizeKeys({
            ...toApiOutput(charge),
            splitChargeId,
            referenceMonthType,
            monthDueDay,
            contractId,
            contractBeginDate,
            contractEndDate,
            configurations: [],
        }),
    });

const createSplitCharge = (
    contractId: string,
    charge: SplitChargeCreationSnapshot
): Promise<SplitChargeSnapshot> =>
    api.request<SplitChargeSnapshot>({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/split_charges`,
        data: decamelizeKeys(toApiOutput(charge)),
    });

const updateSplitCharge = (
    contractId: string,
    splitChargeId: number,
    charge: SplitChargeCreationSnapshot
): Promise<SplitChargeSnapshot> =>
    api.request<SplitChargeSnapshot>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/split_charges/${splitChargeId}`,
        data: decamelizeKeys({
            ...toApiOutput(charge),
            splitChargeId,
        }),
    });

const deleteSplitCharge = (
    contractId: string,
    splitChargeId: number
): Promise<void> =>
    api.request<void>({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/split_charges/${splitChargeId}`,
    });

export {
    getSplitCharges,
    createSplitCharge,
    simulateSplitCharge,
    updateSplitCharge,
    getSplitCharge,
    deleteSplitCharge,
};
